// Pure routing for app-token scope changes, shared by the capability cards
// (`capabilities-section.tsx`) and the Advanced scope editor (`edit-app.tsx`).
// Its own module because `capabilities-section` already imports `ApiTab`
// from `edit-app`, so the reverse import would be circular (FRONT-2817).

/**
 * Whether it's safe to mint a fresh token for this scope set — false when
 * `next` is empty, since minting with an empty `scopes` array makes the
 * server fall back to the broad `DEFAULT_APP_TOKEN_SCOPES` default rather
 * than an actually-empty grant. Exported for direct unit testing (FRONT-912).
 */
export function shouldMintForScopes(next: string[]): boolean {
  return next.length > 0;
}

/** What `applyScopes` should do for a given token/next-scope combination.
 * Exported as a pure function for direct unit testing (FRONT-912). */
export type ApplyScopesAction =
  | { kind: 'noop' }
  | { kind: 'mint'; scopes: string[] }
  | { kind: 'revoke' }
  | { kind: 'save'; scopes: string[] };

/**
 * Decide what `applyScopes` should do. When a token already exists and the
 * caller toggled off the last remaining capability (`next` is empty), the
 * scopes-PATCH route rejects an empty array with a 400 ("At least one scope
 * required (or revoke the token instead)") — so route to revoke instead:
 * no capabilities means no token needed.
 */
export function resolveApplyScopesAction(hasToken: boolean, next: string[]): ApplyScopesAction {
  if (!hasToken) {
    if (!shouldMintForScopes(next)) return { kind: 'noop' };
    return { kind: 'mint', scopes: next };
  }
  if (next.length === 0) return { kind: 'revoke' };
  return { kind: 'save', scopes: next };
}
